import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { adminApi } from '../api/client'

const CODE_TYPES = [
  { value: 'permanent', label: '永久会员', days: 0 },
  { value: 'weekly', label: '周卡 (7天)', days: 7 },
  { value: 'daily', label: '天卡 (1天)', days: 1 },
  { value: 'quota_5', label: 'AI 额度 5 次', quota: 5 },
  { value: 'quota_10', label: 'AI 额度 10 次', quota: 10 },
  { value: 'quota_30', label: 'AI 额度 30 次', quota: 30 },
]

// 筛选值转换为接口参数：quota:N 表示按额度卡次数细分
const codeFilterParams = (filterType) => {
  if (filterType.startsWith('quota:')) {
    return { type: 'quota', days: filterType.split(':')[1] }
  }
  return { type: filterType || undefined }
}

// 卡密类型显示文案（额度卡存储为 type=quota，days 字段为次数）
const codeTypeLabel = (c) =>
  c.type === 'quota'
    ? `额度卡 ${c.days} 次`
    : c.type === 'permanent'
    ? '永久'
    : c.type === 'weekly'
    ? '周卡'
    : '天卡'

export default function Admin() {
  const { user } = useAuth()
  const [codes, setCodes] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // 用户列表
  const [users, setUsers] = useState([])
  const [usersTotal, setUsersTotal] = useState(0)
  const [usersPage, setUsersPage] = useState(1)
  const [usersLoading, setUsersLoading] = useState(false)
  const USERS_PAGE_SIZE = 50

  // 查看指定用户图纸
  const [patternUser, setPatternUser] = useState(null)
  const [userPatterns, setUserPatterns] = useState([])
  const [patternsLoading, setPatternsLoading] = useState(false)

  const openUserPatterns = async (u) => {
    setPatternUser(u)
    setUserPatterns([])
    setPatternsLoading(true)
    try {
      const { data } = await adminApi.getUserPatterns(u.id)
      setUserPatterns(data.patterns || [])
    } catch (err) {
      setError(err.response?.data?.error || '获取图纸失败')
      setPatternUser(null)
    } finally {
      setPatternsLoading(false)
    }
  }

  // 生成卡密表单
  const [genType, setGenType] = useState('weekly')
  const [genCount, setGenCount] = useState(10)
  const [generatedCodes, setGeneratedCodes] = useState([])

  // 筛选
  const [filterType, setFilterType] = useState('')
  const [filterUsed, setFilterUsed] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const PAGE_SIZE = 50

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await adminApi.getStats()
      setStats(data)
    } catch (err) {
      console.error('fetch stats failed', err)
    }
  }, [])

  const fetchCodes = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await adminApi.getCodes({
        ...codeFilterParams(filterType),
        used: filterUsed || undefined,
        page,
        pageSize: PAGE_SIZE,
      })
      setCodes(data.codes || [])
      setTotal(data.total || 0)
    } catch (err) {
      setError(err.response?.data?.error || '获取卡密失败')
    } finally {
      setLoading(false)
    }
  }, [filterType, filterUsed, page])

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true)
    try {
      const { data } = await adminApi.getUsers({ page: usersPage, pageSize: USERS_PAGE_SIZE })
      setUsers(data.users || [])
      setUsersTotal(data.total || 0)
    } catch (err) {
      console.error('fetch users failed', err)
    } finally {
      setUsersLoading(false)
    }
  }, [usersPage])

  const handleGenerate = async () => {
    setError('')
    setSuccess('')
    setGeneratedCodes([])
    try {
      const typeConfig = CODE_TYPES.find((t) => t.value === genType)
      // 额度卡：统一存为 type=quota，days 字段存次数
      const isQuota = Boolean(typeConfig?.quota)
      const { data } = await adminApi.generateCodes(
        isQuota ? 'quota' : genType,
        isQuota ? typeConfig.quota : typeConfig?.days || 7,
        genCount
      )
      setGeneratedCodes(data.codes || [])
      setSuccess(`成功生成 ${data.generated} 个 ${typeConfig?.label} 卡密`)
      fetchCodes()
      fetchStats()
    } catch (err) {
      setError(err.response?.data?.error || '生成卡密失败')
    }
  }

  const handleExport = () => {
    if (codes.length === 0) return
    const lines = ['卡密,类型,时长(天),状态,使用者ID,使用时间,创建时间']
    codes.forEach((c) => {
      lines.push(
        `${c.code},${codeTypeLabel(c)},${c.type === 'quota' ? `${c.days}次` : c.days},${c.is_used ? '已使用' : '未使用'},${c.used_by || ''},${c.used_at ? new Date(c.used_at).toLocaleString() : ''},${new Date(c.created_at).toLocaleString()}`
      )
    })
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `activation_codes_${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  // AI 等待小贴士自定义
  const [tipsText, setTipsText] = useState('')
  const [tipsLoading, setTipsLoading] = useState(false)
  const [tipsMsg, setTipsMsg] = useState('')

  const fetchTips = useCallback(async () => {
    try {
      const { data } = await adminApi.getTips()
      if (Array.isArray(data.tips) && data.tips.length > 0) {
        setTipsText(data.tips.join('\n'))
      }
    } catch (err) {
      console.error('fetch tips failed', err)
    }
  }, [])

  const saveTips = async () => {
    const tips = tipsText.split('\n').map((t) => t.trim()).filter(Boolean)
    if (tips.length === 0) {
      setTipsMsg('❌ 至少保留一条小贴士')
      return
    }
    setTipsLoading(true)
    setTipsMsg('')
    try {
      await adminApi.saveTips(tips)
      setTipsMsg(`✅ 已保存 ${tips.length} 条小贴士，用户端即时生效`)
    } catch (err) {
      setTipsMsg(`❌ ${err.response?.data?.error || '保存失败'}`)
    } finally {
      setTipsLoading(false)
    }
  }

  useEffect(() => {
    if (!user?.isAdmin) return
    fetchStats()
    fetchCodes()
    fetchUsers()
    fetchTips()
  }, [user, fetchStats, fetchCodes, fetchUsers, fetchTips])

  // 生成并复制邀请：选中类型后生成 1 张全新卡密，复制包含网站地址 + 卡密 + 使用方法的邀请文本
  // 注意：不复用已有卡密，避免与已上传到售卖平台的卡密冲突
  const [inviteCopied, setInviteCopied] = useState(false)
  const handleGenerateInvite = async () => {
    setError('')
    try {
      const typeConfig = CODE_TYPES.find((t) => t.value === genType)
      const isQuota = Boolean(typeConfig?.quota)
      const serverType = isQuota ? 'quota' : genType
      const days = isQuota ? typeConfig.quota : typeConfig?.days || 7
      const { data } = await adminApi.generateCodes(serverType, days, 1)
      const newCode = data.codes?.[0]
      if (!newCode) throw new Error('生成失败')

      // 文案不含 https:// 等链接特征，避免各平台审核屏蔽
      const text =
        `🌱 豆田造物 · 拼豆图纸小工具\n\n` +
        `浏览器搜或输入：pindou.scsclub.xyz\n` +
        `注册时填入卡密：${newCode}（${typeConfig?.label}）\n` +
        `老用户：个人中心 → 兑换卡密，输入即可生效`
      try {
        await navigator.clipboard.writeText(text)
      } catch {
        const ta = document.createElement('textarea')
        ta.value = text
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setSuccess(`已生成新卡密 ${newCode} 并复制邀请文本`)
      setInviteCopied(true)
      setTimeout(() => setInviteCopied(false), 2000)
      fetchCodes()
      fetchStats()
    } catch (err) {
      setError(err.response?.data?.error || '生成邀请失败')
    }
  }

  // 导出 TXT：只包含未使用卡密，每行一个（遵循当前类型筛选）
  const handleExportTxt = async () => {
    setError('')
    try {
      const all = []
      let p = 1
      // 循环拉取全部未使用卡密（服务端单页上限 500）
      while (true) {
        const { data } = await adminApi.getCodes({
          ...codeFilterParams(filterType),
          used: 'false',
          page: p,
          pageSize: 500,
        })
        const batch = data.codes || []
        all.push(...batch)
        if (all.length >= (data.total || 0) || batch.length === 0) break
        p += 1
      }
      if (all.length === 0) {
        setError('没有可导出的未使用卡密')
        return
      }
      const text = all.map((c) => c.code).join('\n') + '\n'
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const suffix = filterType ? `_${filterType.replace(':', '_')}` : ''
      link.download = `unused_codes${suffix}_${new Date().toISOString().slice(0, 10)}.txt`
      link.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err.response?.data?.error || '导出 TXT 失败')
    }
  }

  const handleExportGenerated = () => {
    if (generatedCodes.length === 0) return
    const text = generatedCodes.join('\n')
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `new_codes_${new Date().toISOString().slice(0, 10)}.txt`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (!user?.isAdmin) {
    return (
      <div className="mx-auto max-w-md text-center">
        <div className="card py-12">
          <div className="mb-3 text-4xl">🚫</div>
          <h1 className="text-xl font-bold text-cocoa">无权访问</h1>
          <p className="mt-2 text-sm text-cocoa-light">该页面仅管理员可访问</p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-cocoa">管理员后台 🔧</h1>
        <p className="mt-2 text-cocoa-light">卡密生成与管理</p>
      </div>

      {/* 统计卡片 */}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl bg-lavender/20 p-4">
            <p className="text-xs text-cocoa-light">总用户数</p>
            <p className="mt-1 text-2xl font-bold text-cocoa">{stats.users.total}</p>
          </div>
          <div className="rounded-2xl bg-mint/20 p-4">
            <p className="text-xs text-cocoa-light">卡密总数</p>
            <p className="mt-1 text-2xl font-bold text-cocoa">{stats.codes.total}</p>
            <p className="text-xs text-cocoa-light">已使用 {stats.codes.used}</p>
          </div>
          <div className="rounded-2xl bg-peach/20 p-4">
            <p className="text-xs text-cocoa-light">订单总数</p>
            <p className="mt-1 text-2xl font-bold text-cocoa">{stats.orders.total}</p>
            <p className="text-xs text-cocoa-light">已支付 {stats.orders.paid}</p>
          </div>
          <div className="rounded-2xl bg-blush-light/40 p-4">
            <p className="text-xs text-cocoa-light">总收入</p>
            <p className="mt-1 text-2xl font-bold text-cocoa">¥{stats.orders.revenue.toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* 生成卡密 */}
      <div className="card">
        <h2 className="mb-4 text-lg font-bold text-cocoa">生成卡密</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs text-cocoa-light">卡密类型</label>
            <select
              value={genType}
              onChange={(e) => setGenType(e.target.value)}
              className="form-input h-10"
            >
              {CODE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-cocoa-light">数量</label>
            <input
              type="number"
              min={1}
              max={1000}
              value={genCount}
              onChange={(e) => setGenCount(Math.max(1, Math.min(1000, Number(e.target.value))))}
              className="form-input h-10 w-24"
            />
          </div>
          <button
            onClick={handleGenerate}
            className="rounded-full bg-blush px-5 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-blush-dark"
          >
            生成
          </button>
          <button
            onClick={handleGenerateInvite}
            className="rounded-full bg-mint px-5 py-2 text-sm font-semibold text-cocoa shadow-soft transition hover:bg-mint-dark"
            title="生成 1 张全新卡密并复制邀请文本（不会影响已有卡密）"
          >
            {inviteCopied ? '已复制 ✓' : '🔗 生成并复制邀请'}
          </button>
        </div>

        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
        {success && <p className="mt-3 text-sm text-mint-dark">{success}</p>}

        {generatedCodes.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-cocoa">新生成的卡密</span>
              <button
                onClick={handleExportGenerated}
                className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
              >
                导出 TXT
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto rounded-2xl border border-lavender/30 bg-cream-50 p-3">
              {generatedCodes.map((code) => (
                <div key={code} className="font-mono text-sm text-cocoa">{code}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 卡密列表 */}
      <div className="card">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-cocoa">卡密列表</h2>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setPage(1) }}
              className="form-input h-9 text-sm"
            >
              <option value="">全部类型</option>
              <option value="permanent">永久</option>
              <option value="weekly">周卡</option>
              <option value="daily">天卡</option>
              <option value="quota">额度卡（全部）</option>
              <option value="quota:5">额度卡 5 次</option>
              <option value="quota:10">额度卡 10 次</option>
              <option value="quota:30">额度卡 30 次</option>
            </select>
            <select
              value={filterUsed}
              onChange={(e) => { setFilterUsed(e.target.value); setPage(1) }}
              className="form-input h-9 text-sm"
            >
              <option value="">全部状态</option>
              <option value="false">未使用</option>
              <option value="true">已使用</option>
            </select>
            <button
              onClick={handleExportTxt}
              className="rounded-full border border-lavender/60 px-4 py-2 text-sm font-semibold text-cocoa shadow-soft transition hover:bg-lavender/30"
            >
              导出 TXT（仅未使用）
            </button>
            <button
              onClick={handleExport}
              disabled={codes.length === 0}
              className="rounded-full bg-mint px-4 py-2 text-sm font-semibold text-cocoa shadow-soft transition hover:bg-mint-dark disabled:opacity-50"
            >
              导出 CSV
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-cocoa-light">加载中…</p>
        ) : codes.length === 0 ? (
          <div className="rounded-2xl bg-cream-100 py-8 text-center">
            <p className="text-sm text-cocoa-light">暂无卡密</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-2xl border border-lavender/30">
              <table className="w-full text-left text-sm">
                <thead className="bg-cream-100 text-cocoa-light">
                  <tr>
                    <th className="px-4 py-3 font-semibold">卡密</th>
                    <th className="px-4 py-3 font-semibold">类型</th>
                    <th className="px-4 py-3 font-semibold">时长</th>
                    <th className="px-4 py-3 font-semibold">状态</th>
                    <th className="px-4 py-3 font-semibold">使用者</th>
                    <th className="px-4 py-3 font-semibold">使用时间</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-lavender/20 bg-white">
                  {codes.map((c) => (
                    <tr key={c.code} className="transition hover:bg-cream-50">
                      <td className="px-4 py-3 font-mono text-xs text-cocoa">{c.code}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs ${
                          c.type === 'permanent'
                            ? 'bg-peach/30 text-cocoa'
                            : c.type === 'weekly'
                            ? 'bg-mint/30 text-cocoa'
                            : c.type === 'quota'
                            ? 'bg-blush/30 text-cocoa'
                            : 'bg-lavender/30 text-cocoa'
                        }`}>
                          {codeTypeLabel(c)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-cocoa">
                        {c.type === 'quota' ? `${c.days} 次` : c.days === 0 ? '永久' : `${c.days} 天`}
                      </td>
                      <td className="px-4 py-3">
                        {c.is_used ? (
                          <span className="rounded-full bg-cream-200 px-2 py-0.5 text-xs text-cocoa-muted">已使用</span>
                        ) : (
                          <span className="rounded-full bg-mint/30 px-2 py-0.5 text-xs text-mint-dark">未使用</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-cocoa">{c.used_by || '-'}</td>
                      <td className="px-4 py-3 text-xs text-cocoa-light">
                        {c.used_at ? new Date(c.used_at).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            {total > PAGE_SIZE && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-xs text-cocoa-light">
                  第 {page} 页 / 共 {Math.ceil(total / PAGE_SIZE)} 页（{total} 条）
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
                  >
                    上一页
                  </button>
                  <button
                    onClick={() => setPage((p) => (p * PAGE_SIZE < total ? p + 1 : p))}
                    disabled={page * PAGE_SIZE >= total}
                    className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* AI 等待小贴士自定义 */}
      <div className="card">
        <h2 className="mb-2 text-lg font-bold text-cocoa">AI 生图等待小贴士</h2>
        <p className="mb-3 text-xs text-cocoa-light">
          每行一条，生成等待时在按钮中轮播显示。建议每条不超过 10 个字。保存后用户端即时生效。
        </p>
        <textarea
          value={tipsText}
          onChange={(e) => setTipsText(e.target.value)}
          rows={8}
          placeholder={'🔥 熨烫垫烘焙纸\n🎨 深色描边更清晰\n✨ 签到送 AI 额度'}
          className="form-input w-full font-mono text-sm"
        />
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={saveTips}
            disabled={tipsLoading}
            className="rounded-full bg-blush px-5 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-blush-dark disabled:opacity-60"
          >
            {tipsLoading ? '保存中…' : '保存小贴士'}
          </button>
          {tipsMsg && (
            <span className={`text-sm ${tipsMsg.startsWith('✅') ? 'text-mint-dark' : 'text-red-500'}`}>
              {tipsMsg}
            </span>
          )}
        </div>
      </div>

      {/* 用户列表 */}
      <div className="card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-cocoa">用户列表（{usersTotal}）</h2>
          <button
            onClick={fetchUsers}
            className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
          >
            刷新
          </button>
        </div>
        {usersLoading ? (
          <p className="text-sm text-cocoa-light">加载中…</p>
        ) : users.length === 0 ? (
          <div className="rounded-2xl bg-cream-100 py-8 text-center">
            <p className="text-sm text-cocoa-light">暂无用户</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-lavender/30">
            <table className="w-full text-left text-sm">
              <thead className="bg-cream-100 text-cocoa-light">
                <tr>
                  <th className="px-4 py-3 font-semibold">ID</th>
                  <th className="px-4 py-3 font-semibold">邮箱 / 用户名</th>
                  <th className="px-4 py-3 font-semibold">会员</th>
                  <th className="px-4 py-3 font-semibold">AI 额度</th>
                  <th className="px-4 py-3 font-semibold">本月导出</th>
                  <th className="px-4 py-3 font-semibold">连签</th>
                  <th className="px-4 py-3 font-semibold">注册时间</th>
                  <th className="px-4 py-3 font-semibold">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-lavender/20 bg-white">
                {users.map((u) => {
                  const premiumActive = Boolean(u.is_premium) && Number(u.premium_until) > Date.now()
                  const quotaExpired = u.quota > 0 && Number(u.quota_expires_at) > 0 && Number(u.quota_expires_at) <= Date.now()
                  return (
                    <tr key={u.id} className="transition hover:bg-cream-50">
                      <td className="px-4 py-3 text-cocoa">
                        {u.id}
                        {u.is_admin ? (
                          <span className="ml-1 rounded-full bg-blush px-1.5 py-0.5 text-[10px] text-white">管理</span>
                        ) : null}
                        {u.is_merchant ? (
                          <span className="ml-1 rounded-full bg-mint px-1.5 py-0.5 text-[10px] text-cocoa">商家</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-cocoa">{u.email}</p>
                        <p className="text-xs text-cocoa-light">{u.username || '-'}</p>
                      </td>
                      <td className="px-4 py-3">
                        {premiumActive ? (
                          <span className="rounded-full bg-peach/30 px-2 py-0.5 text-xs text-cocoa">
                            至 {new Date(Number(u.premium_until)).toLocaleDateString('zh-CN')}
                          </span>
                        ) : (
                          <span className="rounded-full bg-cream-200 px-2 py-0.5 text-xs text-cocoa-muted">非会员</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-cocoa">
                        {u.quota}
                        {quotaExpired && <span className="ml-1 text-xs text-red-400">已过期</span>}
                      </td>
                      <td className="px-4 py-3 text-cocoa">{u.exports_used}</td>
                      <td className="px-4 py-3 text-cocoa">{u.checkin_streak} 天</td>
                      <td className="px-4 py-3 text-xs text-cocoa-light">
                        {u.created_at ? new Date(Number(u.created_at)).toLocaleString('zh-CN') : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openUserPatterns(u)}
                          className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                        >
                          图纸
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {usersTotal > USERS_PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-cocoa-light">
              第 {usersPage} 页 / 共 {Math.ceil(usersTotal / USERS_PAGE_SIZE)} 页
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                disabled={usersPage <= 1}
                className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
              >
                上一页
              </button>
              <button
                onClick={() => setUsersPage((p) => (p * USERS_PAGE_SIZE < usersTotal ? p + 1 : p))}
                disabled={usersPage * USERS_PAGE_SIZE >= usersTotal}
                className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>
      {/* 用户图纸弹窗 */}
      {patternUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPatternUser(null)}
        >
          <div
            className="max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-cocoa">
                {patternUser.username || patternUser.email} 的图纸（{userPatterns.length}）
              </h3>
              <button
                onClick={() => setPatternUser(null)}
                className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
              >
                关闭
              </button>
            </div>
            {patternsLoading ? (
              <p className="text-sm text-cocoa-light">加载中…</p>
            ) : userPatterns.length === 0 ? (
              <div className="rounded-2xl bg-cream-100 py-8 text-center">
                <p className="text-sm text-cocoa-light">该用户还没有保存过图纸</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {userPatterns.map((p) => (
                  <div key={p.id} className="overflow-hidden rounded-2xl border border-lavender/30 bg-cream-50">
                    <img
                      src={p.image_url}
                      alt={p.title || `pattern-${p.id}`}
                      className="aspect-square w-full object-cover"
                      loading="lazy"
                    />
                    <div className="p-2">
                      <p className="truncate text-xs font-medium text-cocoa">{p.title || '未命名图纸'}</p>
                      <p className="text-[10px] text-cocoa-light">
                        {p.brand || '-'} · {p.width}×{p.height} · {p.is_public ? '公开' : '私有'} · ❤️{p.likes_count || 0}
                      </p>
                      <p className="text-[10px] text-cocoa-light">
                        {p.created_at ? new Date(Number(p.created_at)).toLocaleString('zh-CN') : '-'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
